import React from 'react';
import { Link } from 'react-router-dom';
import './Services.css';

function Services() {
  return (
    <div className="services">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <h1 className="fade-in">Our Services</h1>
          <p className="services-hero-subtitle fade-in">
            Comprehensive IT solutions to drive your business forward
          </p>
        </div>
      </section>

      {/* Software Development */}
      <section className="section section-light" id="software">
        <div className="container">
          <div className="service-detail">
            <div className="service-detail-content">
              <div className="service-badge">Core Service</div>
              <h2>Software Development</h2>
              <p className="service-intro">
                We specialize in building custom web and mobile applications that solve real 
                business problems. Our development process emphasizes quality, scalability, 
                and user experience.
              </p>

              <div className="service-features">
                <div className="feature-item">
                  <h4>🌐 Web Applications</h4>
                  <p>
                    Full-stack web development using React, PHP (Laravel, CakePHP, Yii2), 
                    and Python (Flask, FastAPI). Responsive, fast, and SEO-optimized.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>📱 Mobile Applications</h4>
                  <p>
                    Native and cross-platform mobile apps for iOS and Android. Seamless 
                    user experiences with offline capabilities.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>🏢 Enterprise Solutions</h4>
                  <p>
                    Complex business applications with multi-user support, role-based access, 
                    and integration with existing systems.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>🔌 API Development</h4>
                  <p>
                    RESTful and GraphQL APIs with comprehensive documentation using Swagger/OpenAPI. 
                    Secure, scalable, and well-documented.
                  </p>
                </div>
              </div>

              <div className="tech-stack-detail">
                <h4>Technologies We Use</h4>
                <div className="tech-grid">
                  <div className="tech-column">
                    <h5>Backend</h5>
                    <ul>
                      <li>Python (Flask, FastAPI)</li>
                      <li>PHP (Laravel, CakePHP, Yii2)</li>
                      <li>RESTful APIs</li>
                    </ul>
                  </div>
                  <div className="tech-column">
                    <h5>Frontend</h5>
                    <ul>
                      <li>React</li>
                      <li>JavaScript / Ajax</li>
                      <li>HTML5 & CSS3</li>
                    </ul>
                  </div>
                  <div className="tech-column">
                    <h5>Databases</h5>
                    <ul>
                      <li>MySQL / MariaDB</li>
                      <li>PostgreSQL</li>
                      <li>MongoDB</li>
                    </ul>
                  </div>
                  <div className="tech-column">
                    <h5>DevOps</h5>
                    <ul>
                      <li>Docker</li>
                      <li>Kubernetes</li>
                      <li>Jenkins / Git</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="service-detail-image">
              <div className="image-placeholder">
                <span className="placeholder-icon">💻</span>
                <p>Custom Development</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultancy Services */}
      <section className="section" id="consultancy">
        <div className="container">
          <div className="service-detail reverse">
            <div className="service-detail-image">
              <div className="image-placeholder">
                <span className="placeholder-icon">🎯</span>
                <p>Expert Guidance</p>
              </div>
            </div>

            <div className="service-detail-content">
              <div className="service-badge">Expert Advisory</div>
              <h2>Consultancy Services</h2>
              <p className="service-intro">
                Leverage our 15+ years of experience to make informed technology decisions. 
                We provide strategic guidance focused on application development, system 
                architecture, and technology optimization.
              </p>

              <div className="service-features">
                <div className="feature-item">
                  <h4>🚀 Application Development Consulting</h4>
                  <p>
                    Expert advice on technology stack selection, architecture design, 
                    development methodologies, and best practices for your projects.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>🔄 Legacy System Migration</h4>
                  <p>
                    Strategic planning and execution for modernizing outdated applications. 
                    We assess, plan, and guide the migration with minimal business disruption.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>📊 Business Analysis</h4>
                  <p>
                    Comprehensive analysis of business processes and requirements. We help 
                    translate business needs into technical solutions.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>🏗️ System Architecture Review</h4>
                  <p>
                    Evaluation of existing systems with recommendations for improvements in 
                    performance, security, and scalability.
                  </p>
                </div>
              </div>

              <div className="consultation-areas">
                <h4>Our Consultation Focus Areas</h4>
                <ul className="focus-list">
                  <li>Application Development Strategy</li>
                  <li>Technology Stack Evaluation</li>
                  <li>Legacy Application Modernization</li>
                  <li>Database Design & Optimization</li>
                  <li>API Strategy & Integration</li>
                  <li>Cloud Migration Planning</li>
                  <li>DevOps Implementation</li>
                  <li>Security Best Practices</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IT Equipment Procurement */}
      <section className="section section-light" id="procurement">
        <div className="container">
          <div className="service-detail">
            <div className="service-detail-content">
              <div className="service-badge">Hardware Solutions</div>
              <h2>IT Equipment Procurement</h2>
              <p className="service-intro">
                Professional procurement services for all your IT hardware and equipment needs. 
                We source quality products from trusted suppliers and ensure you get the best 
                value for your investment.
              </p>

              <div className="service-features">
                <div className="feature-item">
                  <h4>🖥️ Hardware Sourcing</h4>
                  <p>
                    Servers, workstations, laptops, and peripherals from leading manufacturers. 
                    We help you choose the right equipment for your needs.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>🌐 Networking Equipment</h4>
                  <p>
                    Routers, switches, firewalls, and wireless access points. Complete network 
                    infrastructure solutions for businesses of all sizes.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>💾 Storage Solutions</h4>
                  <p>
                    NAS devices, SAN systems, and backup solutions. Reliable data storage 
                    infrastructure to protect your critical business information.
                  </p>
                </div>

                <div className="feature-item">
                  <h4>🛠️ Installation & Setup</h4>
                  <p>
                    Professional installation and configuration of procured equipment. 
                    We ensure everything is properly set up and optimized.
                  </p>
                </div>
              </div>

              <div className="procurement-benefits">
                <h4>Why Choose Our Procurement Services</h4>
                <div className="benefits-grid">
                  <div className="benefit-card">
                    <span>💰</span>
                    <p>Competitive pricing through established supplier relationships</p>
                  </div>
                  <div className="benefit-card">
                    <span>✅</span>
                    <p>Quality assurance and warranty management</p>
                  </div>
                  <div className="benefit-card">
                    <span>⚡</span>
                    <p>Fast turnaround times and reliable delivery</p>
                  </div>
                  <div className="benefit-card">
                    <span>🎯</span>
                    <p>Expert guidance on product selection</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="service-detail-image">
              <div className="image-placeholder">
                <span className="placeholder-icon">🖥️</span>
                <p>Quality Equipment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="services-cta">
            <h2>Ready to Get Started?</h2>
            <p>
              Let's discuss your project requirements and how we can help bring your vision to life.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Contact Us Today
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;
